import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/logo.png';
import Button from '../components/ui/Button';
import { CONTACT, FREE_TRIAL_MESSAGE, NAV_LINKS, SITE, whatsappLink } from '../config/site';
import useActiveSection from '../hooks/useActiveSection';
import { cn } from '../lib/utils';

const SECTION_IDS = NAV_LINKS.filter((l) => l.href.includes('#')).map((l) => l.href.split('#')[1]);

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const location = useLocation();
    const menuButtonRef = useRef(null);
    const isHome = location.pathname === '/';

    const activeSection = useActiveSection(SECTION_IDS, { enabled: isHome });

    // Solidify the header background once the page has scrolled past the hero edge.
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Close the mobile menu whenever the route changes.
    useEffect(() => setMenuOpen(false), [location.pathname, location.hash]);

    // While the menu is open: lock body scroll and allow Escape to dismiss it.
    useEffect(() => {
        if (!menuOpen) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                setMenuOpen(false);
                menuButtonRef.current?.focus();
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [menuOpen]);

    const isActive = useCallback(
        (href) => {
            if (href.startsWith('/#')) return isHome && activeSection === href.split('#')[1];
            return location.pathname === href || location.pathname.startsWith(`${href}/`);
        },
        [isHome, activeSection, location.pathname],
    );

    const trialHref = useMemo(() => whatsappLink(FREE_TRIAL_MESSAGE), []);

    return (
        <>
            <a
                href="#contenu-principal"
                className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-full focus:bg-brand focus:px-5 focus:py-2.5 focus:font-semibold focus:text-ink-950"
            >
                Aller au contenu principal
            </a>

            <header
                className={cn(
                    'sticky top-0 z-50 w-full transition-[background-color,border-color,backdrop-filter] duration-300',
                    scrolled || menuOpen
                        ? 'border-b border-line bg-ink-950/85 backdrop-blur-xl'
                        : 'border-b border-transparent bg-transparent',
                )}
            >
                <div className="container flex h-16 items-center justify-between gap-4 md:h-[4.5rem]">
                    <Link to="/" className="flex shrink-0 items-center" aria-label={`${SITE.name} — accueil`}>
                        <img src={logo} alt={SITE.name} width="56" height="56" className="h-11 w-auto rounded-md" />
                    </Link>

                    {/* Desktop navigation */}
                    <nav aria-label="Navigation principale" className="hidden lg:block">
                        <ul className="flex items-center gap-1">
                            {NAV_LINKS.map((link) => (
                                <li key={link.href}>
                                    <Link
                                        to={link.href}
                                        aria-current={isActive(link.href) ? 'page' : undefined}
                                        className={cn(
                                            'relative rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-200',
                                            isActive(link.href)
                                                ? 'text-brand'
                                                : 'text-content-muted hover:text-white',
                                        )}
                                    >
                                        {link.label}
                                        {isActive(link.href) && (
                                            <span className="absolute inset-x-3.5 -bottom-0.5 h-px bg-brand" />
                                        )}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className="flex items-center gap-2 sm:gap-3">
                        <Button
                            href={trialHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outline"
                            size="sm"
                            className="hidden sm:inline-flex"
                        >
                            Test gratuit
                        </Button>

                        <Button as={Link} to="/#forfaits" size="sm">
                            Voir les forfaits
                        </Button>

                        <button
                            ref={menuButtonRef}
                            type="button"
                            onClick={() => setMenuOpen((open) => !open)}
                            aria-expanded={menuOpen}
                            aria-controls="menu-mobile"
                            aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                            className="grid h-10 w-10 place-items-center rounded-full border border-line text-white transition-colors hover:bg-white/[0.06] lg:hidden"
                        >
                            <span className="relative block h-4 w-5" aria-hidden="true">
                                <span
                                    className={cn(
                                        'absolute left-0 block h-0.5 w-5 rounded bg-current transition-transform duration-300 ease-smooth',
                                        menuOpen ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-0',
                                    )}
                                />
                                <span
                                    className={cn(
                                        'absolute left-0 top-1/2 block h-0.5 w-5 -translate-y-1/2 rounded bg-current transition-opacity duration-200',
                                        menuOpen && 'opacity-0',
                                    )}
                                />
                                <span
                                    className={cn(
                                        'absolute left-0 block h-0.5 w-5 rounded bg-current transition-transform duration-300 ease-smooth',
                                        menuOpen ? 'top-1/2 -translate-y-1/2 -rotate-45' : 'bottom-0',
                                    )}
                                />
                            </span>
                        </button>
                    </div>
                </div>

                {/* Mobile menu */}
                <div
                    id="menu-mobile"
                    className={cn(
                        'grid overflow-hidden border-t transition-[grid-template-rows,border-color] duration-300 ease-smooth lg:hidden',
                        menuOpen
                            ? 'grid-rows-[1fr] border-line bg-ink-950/95 backdrop-blur-xl'
                            : 'grid-rows-[0fr] border-transparent',
                    )}
                >
                    <div className="overflow-hidden">
                        <nav aria-label="Navigation mobile" className="container py-4">
                            <ul className="flex flex-col">
                                {NAV_LINKS.map((link) => (
                                    <li key={link.href}>
                                        <Link
                                            to={link.href}
                                            aria-current={isActive(link.href) ? 'page' : undefined}
                                            className={cn(
                                                'flex items-center justify-between border-b border-line py-3.5 text-base font-medium transition-colors',
                                                isActive(link.href) ? 'text-brand' : 'text-content-muted',
                                            )}
                                        >
                                            {link.label}
                                            <svg className="h-4 w-4 opacity-40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </Link>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-5 flex flex-col gap-3">
                                <Button href={trialHref} target="_blank" rel="noopener noreferrer" variant="outline" fullWidth>
                                    Demander un test gratuit
                                </Button>
                                <Button href={whatsappLink()} target="_blank" rel="noopener noreferrer" variant="ghost" fullWidth>
                                    {CONTACT.whatsappDisplay}
                                </Button>
                            </div>
                        </nav>
                    </div>
                </div>
            </header>

            <WhatsAppFloatingButton />
        </>
    );
}

/** Persistent support entry point, kept out of the header's render path. */
function WhatsAppFloatingButton() {
    return (
        <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
                'fixed bottom-5 right-5 z-40 grid h-14 w-14 place-items-center rounded-full',
                'bg-[#25D366] text-white shadow-raised transition-transform duration-200 ease-smooth',
                'hover:scale-105 active:scale-95 print:hidden',
            )}
            aria-label="Contacter le support sur WhatsApp"
        >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967c-.273-.099-.471-.148-.67.15c-.197.297-.767.966-.94 1.164c-.173.199-.347.223-.644.075c-.297-.15-1.255-.463-2.39-1.475c-.883-.788-1.48-1.761-1.653-2.059c-.173-.297-.018-.458.13-.606c.134-.133.298-.347.446-.52c.149-.174.198-.298.298-.497c.099-.198.05-.371-.025-.52c-.075-.149-.669-1.612-.916-2.207c-.242-.579-.487-.5-.669-.51c-.173-.008-.371-.01-.57-.01c-.198 0-.52.074-.792.372c-.272.297-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074c.149.198 2.096 3.2 5.077 4.487c.709.306 1.262.489 1.694.625c.712.227 1.36.195 1.871.118c.571-.085 1.758-.719 2.006-1.413c.248-.694.248-1.289.173-1.413c-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214l-3.741.982l.998-3.648l-.236-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884c2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
        </a>
    );
}
